// Presentaciones (kits): definidas por una receta de productos. No tienen stock
// propio (se fabrican). Regla: al menos un componente; no eliminar si hay unidades
// fabricadas.
import { insforge } from '../insforge';
import { registrarMovimiento } from './movimientos';
import type { ComponenteReceta, Presentacion } from '../../types';

interface DetalleRow {
  id: string;
  presentacion_id: string;
  producto_id: string;
  cantidad: number;
}

export async function listarPresentaciones(): Promise<Presentacion[]> {
  const [pres, det, prods, inv] = await Promise.all([
    insforge.database.from('presentaciones').select('*').order('nombre'),
    insforge.database.from('detalle_presentaciones').select('*'),
    insforge.database.from('productos').select('id, nombre'),
    insforge.database.from('inventario').select('ref_id, stock').eq('tipo', 'presentacion'),
  ]);
  for (const r of [pres, det, prods, inv]) if (r.error) throw new Error(r.error.message);

  const nombreProd = new Map<string, string>();
  for (const p of (prods.data ?? []) as { id: string; nombre: string }[]) nombreProd.set(p.id, p.nombre);

  const totales = new Map<string, number>();
  for (const r of (inv.data ?? []) as { ref_id: string; stock: number }[]) {
    totales.set(r.ref_id, (totales.get(r.ref_id) ?? 0) + Number(r.stock));
  }

  const detalles = (det.data ?? []) as DetalleRow[];
  return ((pres.data ?? []) as Presentacion[]).map((p) => ({
    ...p,
    componentes: detalles
      .filter((d) => d.presentacion_id === p.id)
      .map<ComponenteReceta>((d) => ({
        detalle_id: d.id,
        producto_id: d.producto_id,
        producto_nombre: nombreProd.get(d.producto_id) ?? '(desconocido)',
        cantidad: Number(d.cantidad),
      })),
    stockTotal: totales.get(p.id) ?? 0,
  }));
}

export interface EntradaPresentacion {
  nombre: string;
  foto?: string;
  descripcion?: string;
  estado?: Presentacion['estado'];
  componentes?: { producto_id: string; cantidad: number }[];
}

async function guardarComponentes(presentacionId: string, componentes: { producto_id: string; cantidad: number }[]) {
  await insforge.database.from('detalle_presentaciones').delete().eq('presentacion_id', presentacionId);
  const filas = componentes.map((c) => {
    if (!c.producto_id) throw new Error('Cada componente debe tener un producto.');
    if (Number(c.cantidad) <= 0) throw new Error('La cantidad de cada componente debe ser mayor a cero.');
    return { presentacion_id: presentacionId, producto_id: c.producto_id, cantidad: Number(c.cantidad) };
  });
  if (filas.length) {
    const { error } = await insforge.database.from('detalle_presentaciones').insert(filas);
    if (error) throw new Error(error.message);
  }
}

export async function crearPresentacion(input: EntradaPresentacion): Promise<Presentacion> {
  if (!input.componentes || input.componentes.length === 0) {
    throw new Error('Una presentación debe tener al menos un componente.');
  }
  const { data, error } = await insforge.database
    .from('presentaciones')
    .insert([{ nombre: input.nombre, foto: input.foto ?? '', descripcion: input.descripcion ?? '', estado: input.estado ?? 'Activo' }])
    .select();
  if (error) throw new Error(error.message);
  const pres = (data as Presentacion[])[0];
  await guardarComponentes(pres.id, input.componentes);
  await registrarMovimiento({
    tipo: 'Presentación creada',
    descripcion: 'Se creó la presentación ' + pres.nombre,
    item_tipo: 'presentacion',
    item_id: pres.id,
    item_nombre: pres.nombre,
  });
  return pres;
}

export async function editarPresentacion(id: string, input: EntradaPresentacion): Promise<Presentacion> {
  const { data, error } = await insforge.database
    .from('presentaciones')
    .update({ nombre: input.nombre, foto: input.foto ?? '', descripcion: input.descripcion ?? '', estado: input.estado ?? 'Activo' })
    .eq('id', id)
    .select();
  if (error) throw new Error(error.message);
  const pres = (data as Presentacion[])[0];
  if (input.componentes) {
    if (input.componentes.length === 0) throw new Error('Una presentación debe tener al menos un componente.');
    await guardarComponentes(id, input.componentes);
  }
  await registrarMovimiento({
    tipo: 'Presentación editada',
    descripcion: 'Se editó la presentación ' + pres.nombre,
    item_tipo: 'presentacion',
    item_id: id,
    item_nombre: pres.nombre,
  });
  return pres;
}

export async function eliminarPresentacion(id: string): Promise<void> {
  // Regla: no eliminar si tiene unidades fabricadas (stock > 0).
  const { data: conStock, error: e1 } = await insforge.database
    .from('inventario')
    .select('id')
    .eq('tipo', 'presentacion')
    .eq('ref_id', id)
    .gt('stock', 0)
    .limit(1);
  if (e1) throw new Error(e1.message);
  if (conStock && conStock.length > 0) {
    throw new Error('La presentación tiene unidades fabricadas. Deshaz el stock (devolver componentes) antes de eliminar la definición.');
  }

  const { data: preses } = await insforge.database.from('presentaciones').select('nombre').eq('id', id).limit(1);
  const nombre = (preses as { nombre: string }[] | null)?.[0]?.nombre ?? id;

  // La receta cae por ON DELETE CASCADE; el inventario (polimórfico) a mano.
  await insforge.database.from('inventario').delete().eq('tipo', 'presentacion').eq('ref_id', id);
  const { error } = await insforge.database.from('presentaciones').delete().eq('id', id);
  if (error) throw new Error(error.message);
  await registrarMovimiento({
    tipo: 'Presentación eliminada',
    descripcion: 'Se eliminó la presentación ' + nombre,
    item_tipo: 'presentacion',
    item_id: id,
  });
}
